/**
 * The Mission Record control panel's behaviour, without the panel.
 *
 * Everything the transport buttons and the camera column actually *do*, kept
 * out of `routes/Vcr.tsx` so the route stays JSX and wiring — the same split
 * `flight-scene.ts`/`Fly.tsx` uses. The awkward parts are all here: a camera
 * target that has to drag the view mode outside with it, a scrub that has to
 * re-read the position afterwards, a divergence that has to become a sentence.
 */

import { createSignal, type Accessor } from "solid-js";
import type { CameraTarget } from "../flight/camera-target.ts";
import { nextViewTarget } from "../flight/camera-target.ts";
import type { CameraMode, FlightScene } from "../flight/flight-scene.ts";
import type { PlaybackPosition, PlaybackRunner } from "./playback-runner.ts";
import { DEFAULT_PLAYBACK_RATE } from "./playback-speed.ts";

export const IDLE_POSITION: PlaybackPosition = { tick: 0, ticks: 0, playing: false, ended: false };

/** What the route hands the controller once the scene and the runner exist. */
export interface VcrDeps {
  readonly runner: () => PlaybackRunner | undefined;
  readonly scene: () => FlightScene | undefined;
  readonly cameraMode: Accessor<CameraMode>;
  readonly setCameraMode: (mode: CameraMode) => void;
}

export interface VcrControls {
  readonly position: Accessor<PlaybackPosition>;
  readonly rate: Accessor<number>;
  readonly target: Accessor<CameraTarget>;
  /** Set when this build no longer flies the tape the way it was recorded. */
  readonly warning: Accessor<string | undefined>;
  readonly sync: () => void;
  readonly playPause: () => void;
  readonly stop: () => void;
  readonly seek: (tick: number) => void;
  readonly setRate: (rate: number) => void;
  readonly pickTarget: (target: CameraTarget) => void;
  readonly switchView: () => void;
}

/** The sentence a divergence becomes. Empty while the replay is still faithful. */
export function divergenceWarning(divergedAt: number | null): string | undefined {
  if (divergedAt === null) return undefined;
  return `THIS BUILD LEAVES THE TAPE AT TICK ${divergedAt} — THE RECORDING IS NO LONGER FAITHFUL.`;
}

export function createVcrControls(deps: VcrDeps): VcrControls {
  const [position, setPosition] = createSignal<PlaybackPosition>(IDLE_POSITION);
  const [rate, setRateSignal] = createSignal<number>(DEFAULT_PLAYBACK_RATE);
  const [target, setTarget] = createSignal<CameraTarget>(null);
  const [warning, setWarning] = createSignal<string | undefined>(undefined);

  /** Pulls the runner's own view of where it is into the signals the bar reads. */
  const sync = (): void => {
    const runner = deps.runner();
    if (!runner) return;
    setPosition(runner.position());
    setWarning(divergenceWarning(runner.verification().divergedAt));
  };

  /**
   * Picking somebody else's aeroplane takes you outside it. The original greyed
   * its `OTHERS` column out until `EXTERNAL` was chosen; the same rule, stated
   * as a consequence rather than a prohibition, since there is no cockpit to
   * offer and refusing the press would just look broken.
   */
  /**
   * Stops the tape through the *scene* where there is one: `FlightScene.setPaused`
   * sets the same runner flag and silences the mix with it, so a stopped replay
   * does not keep droning (`audio/pause-gate.ts`). Falls back to the bare runner
   * before the scene mounts — the controls exist from the moment the tape loads,
   * and a machine without WebGPU never gets a scene at all.
   */
  const applyPaused = (paused: boolean): void => {
    const scene = deps.scene();
    if (scene) scene.setPaused(paused);
    else deps.runner()?.setPaused(paused);
  };

  const applyTarget = (next: CameraTarget): void => {
    setTarget(next);
    deps.scene()?.setCameraTarget(next);
    if (next !== null && deps.cameraMode() === "cockpit") {
      deps.setCameraMode("chase");
      deps.scene()?.setCameraMode("chase");
    }
  };

  return {
    position,
    rate,
    target,
    warning,
    sync,
    // Both transport stops go through the *scene*, not straight at the runner:
    // `FlightScene.setPaused` sets the same runner flag and silences the mix with
    // it, so a stopped tape does not keep droning (`audio/pause-gate.ts`). The
    // runner is still poked directly when no scene has mounted yet — a replay
    // opened without WebGPU has controls before it has a picture.
    playPause: () => {
      const runner = deps.runner();
      if (!runner) return;
      applyPaused(!runner.paused());
      sync();
    },
    stop: () => {
      applyPaused(true);
      sync();
    },
    seek: (tick) => {
      deps.runner()?.seek(tick);
      sync();
    },
    setRate: (next) => {
      setRateSignal(next);
      deps.runner()?.setRate(next);
    },
    pickTarget: applyTarget,
    switchView: () => {
      const runner = deps.runner();
      if (!runner) return;
      applyTarget(nextViewTarget(target(), runner.others().map((entry) => entry.id)));
    },
  };
}
